import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Printer } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import Layout from '@/components/layout/Layout';
import CalendarSidebar from './CalendarSidebar';
import TimeGrid from './TimeGrid';
import MonthGrid from './MonthGrid';
import TerminDialog from './TerminDialog';
import RelocationConfirmDialog from './RelocationConfirmDialog';
import { retryOnCollision } from '@/components/schedule/collisionWarning';
import { scrollDeltaToCenter } from '@/lib/selectedRecord';
import { applyHoverDialog, HOVER_LEAVE_MS, HOVER_PIN_MS, hoverPointerShouldPin } from './hoverDialog';
import { useAuth } from '@/context/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { usePermissions } from '@/contexts/PermissionsContext';
import {
  VIEW_TYPES, toISO, minutes, mondayOf, addDays, monthGrid, headlineDate, weekHeader, getKW,
  roomsWeekColumns,
  resolveCalendarLocationDefaults, toggleLocationRooms, toggleRoomInSelection,
  calendarWindowHref,
} from './calendarUtils';

const PREF = 'mamv.calendar.prefs';
const loadPrefs = () => {
  try { return JSON.parse(localStorage.getItem(PREF) || '{}'); } catch { return {}; }
};

export default function Calendar() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeTenant } = useTenant();
  const { calendarRoomsOccupancy } = usePermissions();
  const roleNames = new Set((user?.roles || []).map((role) => role.name));
  const isTeacher = (roleNames.has('teacher') || roleNames.has('lehrer'))
    && !user?.is_super_admin
    && !['admin', 'secretary', 'sekretariat', 'verwaltung', 'management']
      .some((role) => roleNames.has(role));
  const [searchParams, setSearchParams] = useSearchParams();
  const prefs = loadPrefs();
  const viewParam = searchParams.get('view');
  const roomsParam = searchParams.get('rooms');
  const beginParam = searchParams.get('begin');
  const locationParam = searchParams.get('location');
  const teacherParam = searchParams.get('teacher_id');
  const detached = searchParams.get('fenster') === '1';
  const focusCourse = searchParams.get('focus_course');
  const wantAllRooms = roomsParam === 'all';
  const wantAllLocations = locationParam === 'all';

  const [viewType, setViewType] = useState(() => {
    if (viewParam && VIEW_TYPES.some((v) => v.id === viewParam)) return viewParam;
    return prefs.viewType || 'resources-vertical';
  });
  const [raster, setRaster] = useState(prefs.raster || 10);
  const [beginHour, setBeginHour] = useState(() => {
    if (beginParam != null && beginParam !== '') {
      const n = Number(beginParam);
      if (Number.isFinite(n) && n >= 0 && n <= 23) return n;
    }
    return prefs.beginHour ?? 12;
  });
  const [currentDate, setCurrentDate] = useState(() => {
    const d = searchParams.get('date');
    if (d && d !== 'today') {
      const p = new Date(d);
      if (!Number.isNaN(p.getTime())) return p;
    }
    return new Date();
  });
  const [rooms, setRooms] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [locationId, setLocationId] = useState(() => {
    if (wantAllLocations) return null;
    return prefs.locationId || null;
  });
  const [showAllRooms, setShowAllRooms] = useState(wantAllRooms || wantAllLocations);
  const [selectedRoomIds, setSelectedRoomIds] = useState(() => {
    if (wantAllRooms) return [];
    if (Array.isArray(prefs.selectedRoomIds) && prefs.selectedRoomIds.length) {
      return prefs.selectedRoomIds.filter(Boolean);
    }
    return prefs.roomId ? [prefs.roomId] : [];
  });
  const [teacherId, setTeacherId] = useState(
    () => (isTeacher ? user?.teacher_id : null) || teacherParam || prefs.teacherId || null,
  );
  const [teacherQuery, setTeacherQuery] = useState('');
  const [hiddenLocationIds, setHiddenLocationIds] = useState(() => new Set());
  const [locationOrder, setLocationOrder] = useState(() =>
    Array.isArray(prefs.locationOrder) ? prefs.locationOrder : []);
  const [onlyFree, setOnlyFree] = useState(false);
  const [freeFrom, setFreeFrom] = useState('10:00');
  const [freeTo, setFreeTo] = useState(() => {
    const end = searchParams.get('end');
    if (end && /^\d{2}:\d{2}$/.test(end)) return end;
    return prefs.endTime || '21:00';
  });
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, event: null, slot: null, pinned: false, anchor: null });
  const [savedTitles, setSavedTitles] = useState([]);
  const [pendingMove, setPendingMove] = useState(null);
  const [planRevision, setPlanRevision] = useState(0);
  const canSeeRoomOccupancy = Boolean(calendarRoomsOccupancy);
  const occupancyDefaulted = useRef(false);
  const calendarDefaultsApplied = useRef(false);
  const lastTenantId = useRef(activeTenant?.id);

  const view = VIEW_TYPES.find((v) => v.id === viewType) || VIEW_TYPES[0];
  const todayIso = toISO(new Date());

  useEffect(() => {
    localStorage.setItem(PREF, JSON.stringify({
      viewType, raster, beginHour, endTime: freeTo, locationId,
      roomId: selectedRoomIds[0] || null,
      selectedRoomIds, teacherId, locationOrder,
    }));
  }, [viewType, raster, beginHour, freeTo, locationId, selectedRoomIds, teacherId, locationOrder]);

  useEffect(() => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set('view', viewType);
      next.set('date', toISO(currentDate));
      if (teacherId) next.set('teacher_id', teacherId);
      else next.delete('teacher_id');
      next.set('begin', String(beginHour));
      if (freeTo) next.set('end', freeTo);
      return next;
    }, { replace: true });
  }, [viewType, currentDate, teacherId, beginHour, freeTo, setSearchParams]);

  useEffect(() => {
    if (isTeacher) {
      const parts = String(user?.full_name || user?.username || 'Lehrkraft').trim().split(/\s+/);
      setTeachers([{
        id: user?.teacher_id,
        first_name: parts.slice(0, -1).join(' '),
        last_name: parts.at(-1) || 'Lehrkraft',
      }].filter((teacher) => teacher.id));
      setTeacherId(user?.teacher_id || null);
      return;
    }
    api.get('/teachers', { params: { limit: 500 } }).then((r) => {
      const list = [...(r.data || [])].sort((a, b) =>
        `${a.last_name}${a.first_name}`.localeCompare(`${b.last_name}${b.first_name}`, 'de'));
      setTeachers(list);
      setTeacherId((prev) => prev || list[0]?.id || null);
    }).catch(() => {});
  }, [isTeacher, user?.full_name, user?.teacher_id, user?.username]);

  useEffect(() => {
    if (lastTenantId.current !== activeTenant?.id) {
      lastTenantId.current = activeTenant?.id;
      calendarDefaultsApplied.current = false;
      occupancyDefaulted.current = false;
    }
  }, [activeTenant?.id]);

  useEffect(() => {
    if (!canSeeRoomOccupancy || occupancyDefaulted.current) return;
    occupancyDefaulted.current = true;
    setShowAllRooms(true);
    setViewType((prev) => (prev === 'teachers-day' ? 'resources-vertical' : prev));
  }, [canSeeRoomOccupancy]);

  useEffect(() => {
    if (!plan?.rooms?.length) return;
    setRooms((prev) => {
      const same = prev.length === plan.rooms.length && prev.every((r, i) => r.id === plan.rooms[i].id);
      return same ? prev : plan.rooms;
    });
  }, [plan]);

  useEffect(() => {
    if (!rooms.length) return;
    if (showAllRooms || wantAllLocations) {
      setLocationId((prev) => (prev == null ? prev : null));
      setSelectedRoomIds((prev) => (prev.length ? [] : prev));
      return;
    }
    const available = rooms.filter((r) => !hiddenLocationIds.has(r.location_id));
    const validIds = new Set(available.map((r) => r.id));

    setSelectedRoomIds((prev) => {
      const kept = prev.filter((id) => validIds.has(id));
      if (kept.length !== prev.length) return kept;
      if (kept.length > 0) {
        calendarDefaultsApplied.current = true;
        return prev;
      }
      if (calendarDefaultsApplied.current) return prev;
      calendarDefaultsApplied.current = true;
      const resolved = resolveCalendarLocationDefaults({
        rooms: available,
        locationId: null,
        selectedRoomIds: [],
        tenant: activeTenant,
      });
      return resolved.selectedRoomIds;
    });

    setLocationId((prev) => {
      const availableLocs = new Set(available.map((r) => r.location_id));
      if (prev && availableLocs.has(prev)) return prev;
      const resolved = resolveCalendarLocationDefaults({
        rooms: available,
        locationId: null,
        selectedRoomIds: [],
        tenant: activeTenant,
      });
      return resolved.locationId;
    });
  }, [rooms, showAllRooms, wantAllLocations, hiddenLocationIds, activeTenant]);

  useEffect(() => {
    if (!selectedRoomIds.length) return;
    const first = rooms.find((r) => r.id === selectedRoomIds[0]);
    if (first?.location_id && first.location_id !== locationId) {
      setLocationId(first.location_id);
    }
  }, [selectedRoomIds, rooms, locationId]);

  const locations = useMemo(() => {
    const map = new Map();
    for (const r of rooms) {
      if (!r.location_id) continue;
      if (!map.has(r.location_id)) map.set(r.location_id, { id: r.location_id, name: r.location_name || 'Standort' });
    }
    const list = [...map.values()];
    if (!locationOrder.length) {
      return list.sort((a, b) => a.name.localeCompare(b.name, 'de'));
    }
    const idx = new Map(locationOrder.map((id, i) => [id, i]));
    return list.sort((a, b) => {
      const ai = idx.has(a.id) ? idx.get(a.id) : 10000;
      const bi = idx.has(b.id) ? idx.get(b.id) : 10000;
      if (ai !== bi) return ai - bi;
      return a.name.localeCompare(b.name, 'de');
    });
  }, [rooms, locationOrder]);

  const reorderLocations = (fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return;
    const ids = locations.map((l) => l.id);
    const from = ids.indexOf(fromId);
    const to = ids.indexOf(toId);
    if (from < 0 || to < 0) return;
    ids.splice(from, 1);
    ids.splice(to, 0, fromId);
    setLocationOrder(ids);
  };

  const range = useMemo(() => {
    if (view.span === 'week') {
      const mo = mondayOf(currentDate);
      return { start: toISO(mo), end: toISO(addDays(mo, 6)) };
    }
    if (view.span === 'month') {
      const weeks = monthGrid(currentDate);
      return { start: toISO(weeks[0][0]), end: toISO(weeks[5][6]) };
    }
    const iso = toISO(currentDate);
    return { start: iso, end: iso };
  }, [currentDate, view.span]);

  useEffect(() => {
    setLoading(true);
    const params = {
      start_date: range.start,
      end_date: range.end,
      include_students: view.span !== 'month',
    };
    if ((isTeacher && !canSeeRoomOccupancy) || view.axis === 'teachers') {
      if (teacherId) params.teacher_id = teacherId;
    }
    // Ein Raum → Serverfilter; mehrere Räume nebeneinander → alle laden, clientseitig filtern
    if (view.axis === 'rooms' && viewType !== 'resources-vertical' && selectedRoomIds.length === 1) {
      params.room_id = selectedRoomIds[0];
    }
    api.get('/lessons/plan-range', { params })
      .then((r) => setPlan(r.data))
      .catch(() => toast.error('Kalender konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [
    range.start, range.end, view.axis, view.span, viewType, teacherId,
    selectedRoomIds, isTeacher, canSeeRoomOccupancy, planRevision,
  ]);

  const entries = plan?.entries || [];
  const holidays = plan?.holidays || [];

  const locRooms = useMemo(() => {
    const list = rooms.filter((r) => !hiddenLocationIds.has(r.location_id));
    const locIdx = new Map(
      (locationOrder.length
        ? locationOrder
        : [...new Set(list.map((r) => r.location_id))].sort((a, b) => {
            const na = list.find((r) => r.location_id === a)?.location_name || '';
            const nb = list.find((r) => r.location_id === b)?.location_name || '';
            return na.localeCompare(nb, 'de');
          })
      ).map((id, i) => [id, i]),
    );
    return [...list].sort((a, b) => {
      const ai = locIdx.has(a.location_id) ? locIdx.get(a.location_id) : 10000;
      const bi = locIdx.has(b.location_id) ? locIdx.get(b.location_id) : 10000;
      if (ai !== bi) return ai - bi;
      return (a.name || '').localeCompare(b.name || '', 'de');
    });
  }, [rooms, hiddenLocationIds, locationOrder]);

  const selectedRooms = useMemo(() => {
    const available = rooms.filter((r) => !hiddenLocationIds.has(r.location_id));
    if (showAllRooms || wantAllLocations) return locRooms;
    const selected = new Set(selectedRoomIds);
    // Reihenfolge wie in der Sidebar (Standort → Raum), damit Kopfzeilen-Gruppen zusammenhängen
    return locRooms.filter((r) => selected.has(r.id));
  }, [rooms, hiddenLocationIds, locRooms, selectedRoomIds, showAllRooms, wantAllLocations]);

  const toggleLocationSelection = (locId) => {
    setShowAllRooms(false);
    calendarDefaultsApplied.current = true;
    const available = rooms.filter((r) => !hiddenLocationIds.has(r.location_id));
    setSelectedRoomIds((prev) => toggleLocationRooms(locId, available, prev));
    setLocationId(locId);
  };

  const toggleRoomSelection = (id) => {
    setShowAllRooms(false);
    calendarDefaultsApplied.current = true;
    if (viewType === 'rooms-week') {
      setSelectedRoomIds([id]);
    } else {
      setSelectedRoomIds((prev) => toggleRoomInSelection(id, prev));
    }
    const room = rooms.find((r) => r.id === id);
    if (room?.location_id) setLocationId(room.location_id);
  };

  const filteredEntries = useMemo(() => {
    let list = entries;
    if (view.axis === 'rooms' && viewType === 'resources-vertical') {
      if (selectedRoomIds.length) {
        const ids = new Set(selectedRoomIds);
        list = list.filter((e) => e.room_id && ids.has(e.room_id));
      } else if (!showAllRooms && !wantAllLocations) {
        list = [];
      }
    }
    // Räume-Tag/Woche: immer auf gewählte Räume einschränken (auch bei nur 1 Raum)
    if (view.axis === 'rooms' && viewType !== 'resources-vertical' && selectedRoomIds.length >= 1) {
      const ids = new Set(selectedRoomIds);
      list = list.filter((e) => e.room_id && ids.has(e.room_id));
    }
    if (onlyFree && viewType === 'resources-vertical') {
      const from = minutes(freeFrom);
      const to = minutes(freeTo);
      const busy = new Set();
      for (const e of list) {
        if (minutes(e.end) <= from || minutes(e.start) >= to) continue;
        if (e.room_id) busy.add(e.room_id);
      }
      return { list, busy };
    }
    return { list, busy: new Set() };
  }, [entries, view.axis, locationId, viewType, locRooms, selectedRoomIds, onlyFree, freeFrom, freeTo]);

  const dayStart = beginHour * 60;
  const dayEnd = useMemo(() => {
    const chosen = minutes(freeTo);
    if (!Number.isFinite(chosen) || chosen <= dayStart) return Math.min(24 * 60, dayStart + 60);
    return Math.min(24 * 60, chosen);
  }, [freeTo, dayStart]);

  const contextTitle = useMemo(() => {
    if (view.axis === 'teachers') {
      const t = teachers.find((x) => x.id === teacherId);
      return t ? `${t.last_name}, ${t.first_name}`.toUpperCase() : 'LEHRKRAFT';
    }
    const loc = locations.find((l) => l.id === locationId);
    const locName = (loc?.name || '').toUpperCase();
    const locNames = [...new Set(
      selectedRooms.map((r) => (r.location_name || '').toUpperCase()).filter(Boolean),
    )];
    // Standort(e) in der Anthrazit-Leiste; Raumspalten stehen darunter im Grid
    const title = locNames.join(' · ') || locName;
    if (viewType === 'resources-vertical') return title || 'RESSOURCEN';
    return title || 'RÄUME';
  }, [view.axis, viewType, teachers, teacherId, locations, locationId, selectedRooms]);

  const columns = useMemo(() => {
    const list = filteredEntries.list;
    const roomCol = (r, extras = {}) => ({
      id: r.id,
      room_id: r.id,
      label: r.name,
      location_name: r.location_name,
      room_name: r.name,
      group: r.location_name || 'Standort',
      groupKey: r.location_id || 'none',
      groupKind: 'location',
      ...extras,
    });
    if (viewType === 'resources-vertical') {
      let cols = selectedRooms;
      if (onlyFree) cols = cols.filter((r) => !filteredEntries.busy.has(r.id));
      const iso = toISO(currentDate);
      return cols.map((r) => ({
        ...roomCol(r),
        date: iso,
        entries: list.filter((e) => e.date === iso && e.room_id === r.id),
      }));
    }
    if (view.span === 'day') {
      const iso = toISO(currentDate);
      if (view.axis === 'teachers') {
        const t = teachers.find((x) => x.id === teacherId);
        return [{
          id: teacherId || 't',
          label: t ? `${t.last_name}, ${t.first_name}` : 'Lehrkraft',
          date: iso,
          teacher_name: t ? `${t.last_name}, ${t.first_name}` : '',
          entries: list.filter((e) => e.date === iso),
        }];
      }
      return selectedRooms.map((r) => ({
        ...roomCol(r),
        date: iso,
        entries: list.filter((e) => e.date === iso && e.room_id === r.id),
      }));
    }
    if (view.span === 'week') {
      if (view.axis === 'teachers') {
        const mo = mondayOf(currentDate);
        const teacher = teachers.find((x) => x.id === teacherId);
        const teacherName = teacher ? `${teacher.last_name}, ${teacher.first_name}` : '';
        return Array.from({ length: 7 }, (_, i) => {
          const d = addDays(mo, i);
          const iso = toISO(d);
          return {
            id: iso,
            label: weekHeader(d),
            date: iso,
            teacher_name: teacherName,
            entries: list.filter((e) => e.date === iso),
          };
        });
      }
      return roomsWeekColumns(currentDate, selectedRooms[0], list);
    }
    return [];
  }, [viewType, view.span, view.axis, selectedRooms, teachers, teacherId, currentDate, filteredEntries, onlyFree]);

  useLayoutEffect(() => {
    if (loading || view.span === 'month' || !focusCourse) return undefined;
    const block = document.querySelector(
      `[data-testid="calendar-block-${focusCourse}-${toISO(currentDate)}"]`,
    );
    const grid = document.querySelector('[data-testid="calendar-time-grid"]');
    if (!block || !grid) return undefined;
    const next = scrollDeltaToCenter(
      grid.getBoundingClientRect(),
      block.getBoundingClientRect(),
      grid.scrollTop,
      grid.scrollLeft,
    );
    grid.scrollTop = next.top;
    grid.scrollLeft = next.left;
  }, [loading, focusCourse, currentDate, columns, view.span]);

  const skipBeginScroll = useRef(true);
  useLayoutEffect(() => {
    if (skipBeginScroll.current) {
      skipBeginScroll.current = false;
      return;
    }
    const grid = document.querySelector('[data-testid="calendar-time-grid"]');
    if (grid) grid.scrollTop = 0;
  }, [beginHour]);

  const loadSavedTitles = () => {
    api.get('/locations/reservation-titles')
      .then((response) => setSavedTitles(response.data?.custom || []))
      .catch(() => {});
  };

  useEffect(() => {
    loadSavedTitles();
  }, [activeTenant?.id]);

  const closeDialog = () => {
    if (leaveTimer.current != null) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }
    setDialog({ open: false, event: null, slot: null, pinned: false, anchor: null });
  };

  const saveReservation = async (form) => {
    const start = (form.start || '').slice(0, 5);
    const end = (form.end || '').slice(0, 5);
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const duration = (eh * 60 + em) - (sh * 60 + sm);
    if (!(form.title || '').trim()) {
      toast.error('Bezeichnung ist ein Pflichtfeld');
      return;
    }
    if (!form.room_id) {
      toast.error('Bitte einen Raum wählen');
      return;
    }
    if (!form.date || !start || !end || !(duration > 0)) {
      toast.error('Beginn und Ende prüfen');
      return;
    }
    if (form.recurrence !== 'once' && !form.valid_until) {
      toast.error('Für eine Wiederholung bitte das Datum „bis“ setzen.');
      return;
    }
    const body = {
      title: form.title.trim(),
      date: form.date,
      start_time: start,
      duration_minutes: duration,
      notes: form.notes || null,
      teacher_id: form.teacher_id || '',
      room_id: form.room_id,
      recurrence: form.recurrence || 'once',
      valid_until: form.recurrence === 'once' ? null : form.valid_until,
      scope: form.scope || 'this',
    };
    const send = (allow) => {
      const payload = { ...body, allow_collisions: allow };
      if (form.reservation_id) return api.put(`/locations/room-reservations/${form.reservation_id}`, payload);
      return api.post(`/locations/rooms/${form.room_id}/reservations`, payload);
    };
    try {
      const saved = await retryOnCollision(() => send(false), () => send(true));
      if (!saved) return;
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Reservierung konnte nicht gespeichert werden');
      return;
    }
    toast.success('Reservierung gespeichert');
    closeDialog();
    loadSavedTitles();
    setPlanRevision((value) => value + 1);
  };

  const deleteReservation = async (form) => {
    if (!form?.reservation_id) return;
    const scope = form.scope || 'this';
    if (!window.confirm('Reservierung löschen?')) return;
    try {
      await api.delete(`/locations/room-reservations/${form.reservation_id}`, { params: { scope } });
      toast.success('Reservierung gelöscht');
      closeDialog();
      setPlanRevision((value) => value + 1);
    } catch {
      toast.error('Reservierung konnte nicht gelöscht werden');
    }
  };

  const leaveTimer = useRef(null);
  const clearLeave = () => {
    if (leaveTimer.current != null) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }
  };
  useEffect(() => clearLeave, []);

  const openEvent = (e) => {
    clearLeave();
    setDialog((current) => applyHoverDialog(current, { type: 'click', event: e }));
  };
  const previewEvent = (e, anchor) => {
    clearLeave();
    setDialog((current) => applyHoverDialog(current, { type: 'hover', event: e, anchor }));
  };
  const endPreview = () => {
    clearLeave();
    leaveTimer.current = setTimeout(() => {
      setDialog((current) => applyHoverDialog(current, { type: 'leave' }));
    }, HOVER_LEAVE_MS);
  };
  useEffect(() => {
    if (!dialog.open || dialog.pinned) return undefined;
    let pinTimer = null;
    let wasResting = false;
    const clearPin = () => {
      if (pinTimer != null) {
        window.clearTimeout(pinTimer);
        pinTimer = null;
      }
    };
    const panelRect = () => document.querySelector('[data-testid="calendar-termin-dialog"]')?.getBoundingClientRect() || null;
    const overEvent = (x, y) => {
      const hit = document.elementFromPoint(x, y);
      return Boolean(hit && hit.closest && hit.closest('[data-calendar-event]'));
    };
    const scheduleLeave = () => {
      if (leaveTimer.current != null) return;
      leaveTimer.current = window.setTimeout(() => {
        leaveTimer.current = null;
        setDialog((current) => applyHoverDialog(current, { type: 'leave' }));
      }, HOVER_LEAVE_MS);
    };
    const onMove = (event) => {
      const overBlock = overEvent(event.clientX, event.clientY);
      const inside = hoverPointerShouldPin(
        { x: event.clientX, y: event.clientY },
        panelRect(),
        { overBlock },
      );
      if (!inside) {
        clearPin();
        if (wasResting && !overBlock) {
          wasResting = false;
          scheduleLeave();
        }
        if (overBlock) wasResting = false;
        return;
      }
      wasResting = true;
      if (leaveTimer.current != null) {
        window.clearTimeout(leaveTimer.current);
        leaveTimer.current = null;
      }
      clearPin();
      pinTimer = window.setTimeout(() => {
        pinTimer = null;
        if (leaveTimer.current != null) {
          window.clearTimeout(leaveTimer.current);
          leaveTimer.current = null;
        }
        setDialog((current) => applyHoverDialog(current, { type: 'pin' }));
      }, HOVER_PIN_MS);
    };
    const onDown = (event) => {
      const inside = hoverPointerShouldPin(
        { x: event.clientX, y: event.clientY },
        panelRect(),
        { overBlock: false },
      );
      if (!inside) return;
      event.preventDefault();
      event.stopPropagation();
      clearPin();
      if (leaveTimer.current != null) {
        window.clearTimeout(leaveTimer.current);
        leaveTimer.current = null;
      }
      setDialog((current) => applyHoverDialog(current, { type: 'pin' }));
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mousedown', onDown, true);
    return () => {
      clearPin();
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mousedown', onDown, true);
    };
  }, [dialog.open, dialog.pinned]);

  const openSlot = (slot) => {
    clearLeave();
    const hh = String(Math.floor(slot.minutes / 60)).padStart(2, '0');
    const mm = String(slot.minutes % 60).padStart(2, '0');
    setDialog({
      open: true,
      pinned: true,
      anchor: null,
      event: null,
      slot: {
        date: slot.date || toISO(currentDate),
        start: `${hh}:${mm}`,
        end: `${String(Math.floor((slot.minutes + 30) / 60)).padStart(2, '0')}:${String((slot.minutes + 30) % 60).padStart(2, '0')}`,
        location_id: slot.location_id,
        location_name: slot.location_name,
        room_id: slot.room_id,
        room_name: slot.room_name,
        teacher_name: slot.teacher_name,
      },
    });
  };

  const page = (
    <>
      <div className={`flex min-w-0 max-w-full flex-col ${detached ? 'h-screen' : 'h-[calc(100vh-7rem)]'}`} data-testid="calendar-page" data-calendar-window={detached ? 'detached' : 'embedded'}>
        <header className="shrink-0 h-11 bg-brand-anthracite text-white flex items-center px-2 gap-2 print:bg-white print:text-slate-900">
          <button type="button" className="h-8 px-3 rounded bg-white/15 hover:bg-white/25 text-xs font-semibold"
                  onClick={() => { if (detached) window.close(); else navigate('/'); }} data-testid="calendar-close">Schließen</button>
          <div className="text-xs font-bold tracking-wide truncate px-2 py-1 bg-black/20 rounded" data-testid="calendar-context">
            {contextTitle}
          </div>
          <div className="flex items-center gap-1 ml-auto">
            {!detached && (
              <button type="button" className="h-8 px-2 rounded bg-white/15 hover:bg-white/25 text-xs font-semibold"
                      data-testid="calendar-popout" title="Kalender in eigenem Fenster öffnen"
                      onClick={() => window.open(calendarWindowHref(searchParams), 'mamv-kalender')}>
                Eigenes Fenster
              </button>
            )}
            <button type="button" className="p-1.5 hover:bg-white/15 rounded" title="Drucken" onClick={() => window.print()}>
              <Printer className="w-4 h-4" />
            </button>
            {!isTeacher && <button type="button" className="p-1.5 hover:bg-white/15 rounded" title="Reservierung"
                    data-testid="calendar-add"
                    onClick={() => {
                      const room = selectedRooms[0];
                      openSlot({
                        minutes: beginHour * 60 + 120,
                        date: toISO(currentDate),
                        room_id: room?.id,
                        room_name: room?.name,
                        location_id: room?.location_id,
                        location_name: room?.location_name,
                      });
                    }}>
              <Plus className="w-4 h-4" />
            </button>}
            <div className="text-xs font-bold tracking-widest pl-3" data-testid="calendar-headline">
              {headlineDate(currentDate)}
              {view.span !== 'day' ? ` · KW ${getKW(currentDate)}` : ''}
            </div>
          </div>
        </header>

        {canSeeRoomOccupancy && (
          <div className="shrink-0 bg-slate-50 border-b border-slate-200 px-3 py-1.5 text-xs text-slate-600" data-testid="calendar-occupancy-banner">
            {calendarRoomsOccupancy === 'busy' && 'Raumbelegung: frei oder belegt, ohne personenbezogene Daten.'}
            {calendarRoomsOccupancy === 'course' && 'Raumbelegung: Unterricht/Kurs sichtbar, ohne Schüler:innen.'}
            {calendarRoomsOccupancy === 'students' && 'Raumbelegung: inkl. beteiligter Schüler:innen.'}
            {' '}Eigene Termine bleiben vollständig sichtbar.
          </div>
        )}

        {plan?.is_holiday && (
          <div className="shrink-0 bg-blue-50 border-b border-blue-200 px-3 py-1.5 text-xs text-blue-900" data-testid="calendar-holiday-banner">
            {plan.holiday?.name || 'Unterrichtsfrei'} — an diesem Tag wird kein Regelunterricht erteilt.
          </div>
        )}

        <div className="flex min-h-0 min-w-0 flex-1">
          <CalendarSidebar
            viewType={viewType} setViewType={setViewType}
            raster={raster} setRaster={setRaster}
            beginHour={beginHour} setBeginHour={setBeginHour}
            currentDate={currentDate} setCurrentDate={setCurrentDate}
            locations={locations} rooms={rooms} teachers={teachers}
            selectedRoomIds={selectedRoomIds}
            onToggleLocation={toggleLocationSelection}
            onToggleRoom={toggleRoomSelection}
            teacherId={teacherId} setTeacherId={setTeacherId}
            teacherQuery={teacherQuery} setTeacherQuery={setTeacherQuery}
            hiddenLocationIds={hiddenLocationIds}
            setHiddenLocationIds={(updater) => {
              if (typeof updater === 'function') {
                setHiddenLocationIds((prev) => {
                  const next = updater(prev);
                  const added = [...next].filter((id) => !prev.has(id));
                  if (added.length) {
                    setSelectedRoomIds((roomPrev) => roomPrev.filter((rid) => {
                      const room = rooms.find((r) => r.id === rid);
                      return !added.includes(room?.location_id);
                    }));
                  }
                  return next;
                });
              } else {
                setHiddenLocationIds(updater);
              }
            }}
            onReorderLocations={reorderLocations}
            onlyFree={onlyFree} setOnlyFree={setOnlyFree}
            freeFrom={freeFrom} setFreeFrom={setFreeFrom}
            freeTo={freeTo} setFreeTo={setFreeTo}
          />
          <div className="flex-1 min-w-0 flex flex-col">
            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-red" />
              </div>
            ) : view.span === 'month' ? (
              <MonthGrid
                weeks={monthGrid(currentDate)}
                entries={filteredEntries.list}
                holidays={holidays}
                currentDate={currentDate}
                todayIso={todayIso}
                onEventClick={openEvent}
                onEventHover={previewEvent}
                onEventHoverEnd={endPreview}
                hoverDelay={dialog.open && !dialog.pinned ? 0 : undefined}
                onDayClick={(d) => { setCurrentDate(d); setViewType(view.axis === 'teachers' ? 'teachers-day' : 'rooms-day'); }}
              />
            ) : (
              <TimeGrid
                columns={columns}
                dayStart={dayStart}
                dayEnd={dayEnd}
                raster={raster}
                todayIso={todayIso}
                onEventClick={openEvent}
                onEventHover={previewEvent}
                onEventHoverEnd={endPreview}
                hoverDelay={dialog.open && !dialog.pinned ? 0 : undefined}
                onSlotClick={isTeacher ? undefined : openSlot}
                onEventDrop={(event, target) => setPendingMove({ event, target })}
                focusCourseId={focusCourse}
              />
            )}
          </div>
        </div>
      </div>
      <TerminDialog
        open={dialog.open}
        onClose={closeDialog}
        event={dialog.event}
        slot={dialog.slot}
        locations={locations}
        rooms={rooms}
        teachers={teachers}
        savedTitles={savedTitles}
        pinned={dialog.pinned}
        anchor={dialog.anchor}
        onSave={saveReservation}
        onDelete={deleteReservation}
      />
      <RelocationConfirmDialog
        move={pendingMove}
        onClose={() => setPendingMove(null)}
        onSaved={() => {
          setPendingMove(null);
          setPlanRevision((value) => value + 1);
          toast.success('Termin wurde verschoben');
        }}
      />
    </>
  );
  return detached ? page : <Layout>{page}</Layout>;
}
